/**
 * The person detail body — sections 2 to 5 of the drill-through.
 *
 * Section 3 is a TABLE, not sparklines — at 190px a sparkline hides the numbers, and
 * the numbers are the point. Each cell is coloured against the peer average its points
 * are scored on (`monthCellReference`), not a global scale.
 */
import { useMemo, useState } from 'react'
import type { Person, Derived, MetricKey, Standing } from '@/data/selectors'
import {
  activeInMonth,
  activeInRange,
  standing,
  peerMean,
  scoreStanding,
  METRIC_BY_KEY,
  NOT_RANKED_TERMINATED,
} from '@/data/selectors'
import { availableSupervisorViewMetrics, teamRanking } from '@/measures/teams'
import { useAppData } from '@/data/AppData'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { useOpenTouchDetail } from '@/data/touchDrill'
import { useOpenClaimDetail, type ClaimDrill } from '@/data/claimDrill'
import { money0, money2, int, pct, dec, hours, dateLong, monthSpan, monthYear } from '@/format'
import { fmt, Info, LowVolumeTag } from './Bits'
import { ScoreTip } from './ScoreTip'
import { useTrailingCaption, useTrailingScore } from './TrailingScore'
import { define } from '@/measures/definitions'
import {
  formatPoints,
  scoreGaps,
  unscoredReason,
  SCORE_MAX,
  type ScoreKey,
} from '@/measures/scorecard'
import { MIN_COHORT } from '@/measures/peer'
import { cellTone, monthCellReference } from './monthCellTone'
import { rankShort, rankState, rankText, titlePlural } from '@/measures/rankText'
import { AGE_BUCKETS, ageCount, divide, touchBase, touchedBase, type AgeBucketKey } from '@/measures/core'
import { monthScorer, MONTH_RULES } from '@/measures/monthScore'
import { COLLECTION_LABEL, collectionWindow, describeWindow } from '@/measures/collection'
import { volumeNote } from '@/measures/claimVolume'
import { PersonComments } from '@focalpointcare/review-drawer'
import { OpenPilePanel } from './OpenPilePanel'
import { FacilityLink } from './FacilityLink'
import { ExportBar } from './ExportBar'
import { useFacilityData, FACILITY_MODELED } from '@/data/facilityData'
import { personFacilityShares } from '@/measures/facilities'
import type { Definition } from '@/measures/definitions'
import type { ExportColumn } from '@/ui/export'

const MONTH_LABEL = (m: string) =>
  new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' })

/**
 * Row definitions for the month-by-month grid. Metrics down, months across.
 *
 * Ordered as the approved scorecard orders its lines — the scored measures first, in
 * descending point value, then the rest. "Cost per resolved claim" used to lead this
 * table and has been removed: it is the one cost measure nothing in the app is scored
 * on, computed on a denominator that sits ~16% away from the billed one every other
 * figure here uses, and putting it first taught a reader the wrong number.
 */
type MonthRow = Person['months'][number]

/**
 * The Ledger's month rows, in its order. `score` names the monthly scorecard rule a
 * row earns points on, so a cell can show what that month's value would score.
 *
 * Exported for the test that pins the labor rows as never ranked.
 */
export const MONTH_ROWS: {
  key: string
  label: string
  higherIsBetter: boolean
  get: (r: MonthRow) => number | null
  format: (v: number | null) => string
  score?: ScoreKey
  /**
   * A ratio's "Your avg": total over total across the months, never the mean of the
   * monthly rates (MEASURE-RULES §3.1). For cost per claim that is the headline
   * itself; a plain mean let one thin month at hundreds of dollars a claim drag the
   * average far from it (review #156, 2026-10-06).
   */
  pooled?: (rows: readonly MonthRow[]) => number | null
  /**
   * Shown, never judged: no colour, no rank against the title. Pay is not a
   * performance measure — a month in green for being paid less is a verdict nobody
   * asked for ("please, let's not rank their labor", 2026-09-23).
   */
  neutral?: boolean
}[] = [
  {
    key: 'cpcb',
    label: 'Cost per claim',
    higherIsBetter: false,
    get: (r) => (r.billedClaims === 0 ? null : r.claimLabor / r.billedClaims),
    format: (v) => money2(v),
    score: 'costPerClaim',
    pooled: (rows) => divide(rows.reduce((a, r) => a + r.claimLabor, 0), rows.reduce((a, r) => a + r.billedClaims, 0)),
  },
  {
    key: 'billed',
    label: 'Billed claims',
    higherIsBetter: true,
    get: (r) => r.billedClaims,
    format: (v) => int(v),
    score: 'claims',
  },
  {
    key: 'coll',
    label: COLLECTION_LABEL,
    higherIsBetter: true,
    get: (r) => r.collectedPct,
    format: (v) => pct(v),
    score: 'collectedPct',
  },
  {
    key: 'rvb',
    label: 'Claims billed vs resolved %',
    higherIsBetter: true,
    get: (r) => (r.billedClaims === 0 ? null : r.resolvedClaims / r.billedClaims),
    format: (v) => pct(v),
    score: 'closedClaimPct',
    pooled: (rows) => divide(rows.reduce((a, r) => a + r.resolvedClaims, 0), rows.reduce((a, r) => a + r.billedClaims, 0)),
  },
  {
    key: 'zeroTouch',
    label: '0% touch share',
    higherIsBetter: true,
    get: (r) => divide(r.resolvedNoTouch, touchBase(r)),
    format: (v) => pct(v),
    score: 'zeroTouchShare',
    pooled: (rows) => divide(rows.reduce((a, r) => a + r.resolvedNoTouch, 0), rows.reduce((a, r) => a + touchBase(r), 0)),
  },
  {
    key: 'wk',
    label: 'Open claims worked in month %',
    higherIsBetter: true,
    get: (r) => (r.openClaims === 0 ? null : r.openClaimsWorked / r.openClaims),
    format: (v) => pct(v),
    score: 'openWorkedPct',
    pooled: (rows) => divide(rows.reduce((a, r) => a + r.openClaimsWorked, 0), rows.reduce((a, r) => a + r.openClaims, 0)),
  },
  {
    key: 'facm',
    label: 'Facilities handled',
    higherIsBetter: true,
    get: (r) => r.facilities,
    format: (v) => int(v),
    score: 'facilities',
  },
  /*
   * The open pile at each month end (review #401, 2026-10-12: "open claims at month end
   * … how many open claims they had on worklist every month"): `FactOpenClaims` at the
   * month's snapshot, system worklists left out — the base of the row above's worked %.
   * The running month's snapshot is the day it was loaded. Ranked and coloured like the
   * person page's open-claims count: a smaller pile is better, and no pile is no figure.
   */
  {
    key: 'openme',
    label: 'Open at month end',
    higherIsBetter: false,
    get: (r) => (r.openClaims > 0 ? r.openClaims : null),
    format: (v) => int(v),
  },
  {
    key: 'resolved',
    label: 'Resolved claims',
    higherIsBetter: true,
    get: (r) => r.resolvedClaims,
    format: (v) => int(v),
  },
  {
    key: 'touches',
    label: 'Touches per touched claim',
    higherIsBetter: false,
    get: (r) => divide(r.resolvedTouches, touchedBase(r)),
    format: (v) => dec(v),
    pooled: (rows) => divide(rows.reduce((a, r) => a + r.resolvedTouches, 0), rows.reduce((a, r) => a + touchedBase(r), 0)),
  },
  // The labor block: pay WITHOUT benefit load and the hours behind it, from payroll
  // (2026-09-23: "their hours, their OT and their PTO… don't include their benefit
  // load"). Not `claimLabor` — that is benefit-loaded, and stays cost per claim's
  // numerator in the first row. A bundle without these fields shows "—".
  {
    key: 'labor',
    label: 'Payroll labor',
    higherIsBetter: false,
    get: (r) => r.payroll ?? null,
    format: (v) => money0(v),
    neutral: true,
  },
  {
    key: 'worked',
    label: 'Total worked (hrs)',
    higherIsBetter: false,
    get: (r) => r.laborHours ?? null,
    format: (v) => hours(v),
    neutral: true,
  },
  {
    key: 'ot',
    label: 'OT (hrs)',
    higherIsBetter: false,
    get: (r) => r.otHours ?? null,
    format: (v) => hours(v),
    neutral: true,
  },
  {
    key: 'pto',
    label: 'PTO (hrs)',
    higherIsBetter: false,
    get: (r) => r.ptoHours ?? null,
    format: (v) => hours(v),
    neutral: true,
  },
]

/** Month row → the measure it shows, for its definition. */
const ROW_DEFINITION: Record<string, string> = {
  cpcb: 'costPerClaim',
  billed: 'billedClaims',
  coll: 'collectedPct',
  rvb: 'closedClaimPct',
  zeroTouch: 'zeroTouchShare',
  wk: 'openWorkedPct',
  facm: 'facilities',
  openme: 'openAtMonthEnd',
  resolved: 'resolvedClaims',
  touches: 'touchesPerClaim',
  labor: 'personPayroll',
  worked: 'hoursWorked',
  ot: 'otHours',
  pto: 'ptoHours',
}

/**
 * What a claims measure opens in Claim detail (review #227, 2026-10-08: "all metrics
 * + month to month view i can drill to the corresponding claims … and then to the
 * touches on the claim itself"). Keyed by both the month rows' keys and the seven
 * measures'. Claim detail lists billed claims by bill date, so:
 *
 *  - cost per claim, billed claims, billed vs resolved % and facilities read the
 *    claims billed in the month (or the range) — billed vs resolved's denominator,
 *    each claim's Status column its numerator's side of it;
 *  - collection rate reads the bill months its window covers (M−8 … M−3), which can
 *    reach back before the range;
 *  - resolved claims, 0% touch share and touches per touched claim read the claims
 *    RESOLVED in the month (or the range), billed any month — the rows their figures
 *    count (review #397, MEASURE-RULES §5.1). Claim detail opens them "Resolved in"
 *    those months, so the count is the cell's resolved claims, and the touch buckets'
 *    base.
 *
 * Open claims worked % is the open pile, not billed claims, and the labor rows are
 * payroll; neither opens Claim detail. Open at month end opens the pile, for the
 * snapshot month only (see `pileOf`).
 */
const CLAIM_DRILL: Record<string, { window?: true; resolved?: true }> = {
  cpcb: {},
  costPerClaim: {},
  billed: {},
  billedPerMonth: {},
  rvb: {},
  closedClaimPct: {},
  facm: {},
  facilities: {},
  coll: { window: true },
  collectedPct: { window: true },
  zeroTouch: { resolved: true },
  zeroTouchShare: { resolved: true },
  touches: { resolved: true },
  resolved: { resolved: true },
}

/**
 * What a claims measure's cell opens in Claim detail, less whose claims: one month's
 * (`month`), or the range's (`range`) for its "Your avg". Null where the measure is
 * not claims. Exported for the test that pins the resolved basis's drill (review #397).
 */
export function claimDrillOf(
  key: string,
  month: string | null,
  range: readonly string[],
): Pick<ClaimDrill, 'months' | 'resolvedIn'> | null {
  const d = CLAIM_DRILL[key]
  if (!d) return null
  const first = month ?? range[0]
  const last = month ?? range[range.length - 1]
  if (!first || !last) return null
  // The resolved basis opens by resolution month, every month of the range for its
  // "Your avg" (review #397).
  if (d.resolved) return { resolvedIn: month ? [month] : [...range] }
  const months = d.window ? collectionWindow(first, last).months : month ? [month] : undefined
  return months ? { months } : {}
}

/**
 * Which kept pile an Open at month end cell opens (review #401), if any. open.json keeps
 * two: the snapshot's (`Dataset.worklistSnapshot`), which is today's and is the cell
 * only when it is the person's current pile; and, when the snapshot is the running
 * month, the latest closed month end's (`Dataset.worklistMonthEnd`) — the grid's last
 * month, since the running month has no month row until its payroll loads. The build
 * ships a month-end list only when it adds up to the cell. Earlier month ends are
 * counts only. Exported for its test.
 */
export function pileFor(
  month: string,
  snapshot: string | null | undefined,
  monthEnd: string | null | undefined,
  monthOpen: number | undefined,
  personOpen: number,
): 'today' | 'monthEnd' | null {
  if (!monthOpen) return null
  if (month === snapshot && monthOpen === personOpen) return 'today'
  if (month === monthEnd) return 'monthEnd'
  return null
}

/** Why an Open at month end cell does not open its claims. */
const PILE_NOTE = 'only the latest month end’s open claims are kept as a list'

/**
 * Sections 2–5 of the detail: standing in job title, the month grid, the open
 * worklist, and the money. Shared by the drill-through panel and the Employee view
 * page so the two can never drift apart.
 */
/**
 * One open-worklist row's rank in the job title and its peer average (review #159,
 * 2026-10-07: "add on employee open claims-peer average, rank"). The same pool and
 * floor as every other rank (`standing`, `peerMean`; MEASURE-RULES §10.7): no data at
 * the bottom, nothing under five.
 */
interface PileMetric {
  get: (p: Person) => number | null
  lowerIsBetter: boolean
  format: (v: number) => string
}

function usePile(person: Person, metric: PileMetric) {
  const { scope } = useAppData()
  const s = standing(scope.cohorts, person, metric)
  const avg = peerMean(scope.cohorts, person, metric)
  return { s, avg }
}

function PileRank({ s, label, value }: { s: Standing; label: string; value: string }) {
  const { derived } = useAppData()
  const tone = s.band === 'good' ? 'var(--good-ink)' : s.band === 'crit' ? 'var(--crit-ink)' : undefined
  return (
    <td
      className="r mono"
      title={`${label}: ${value}${rankText(s, monthSpan(derived.months))}`}
      style={{ color: tone, fontWeight: tone ? 600 : undefined }}
    >
      {s.rank === null ? (
        <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
          {rankState(s) === 'noData' ? rankShort(s) : '—'}
        </span>
      ) : (
        <>
          rank {s.rank}
          <span style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 400 }}>/{s.of} in title</span>
        </>
      )}
    </td>
  )
}

/** The peer average beside the bar, in words; its tick is on the bar itself. */
function PeerAvg({ avg, s, format }: { avg: number | null; s: Standing; format: (v: number) => string }) {
  return (
    <td
      className="r mono"
      data-testid="pile-peer-avg"
      title={
        avg === null
          ? `No peer average: fewer than ${MIN_COHORT} ${s.group} have a figure`
          : `Peer average: the plain mean over the ${s.of - s.noData} ${s.group} with a figure, this person included`
      }
    >
      {avg === null ? '—' : <>peer avg {format(avg)}</>}
    </td>
  )
}

/**
 * Which way each pile bucket ranks. Worked within the last 30 days is good, so more is
 * better; worked over 30 days ago or never is the neglect, so less is better.
 */
const AGE_LOWER_IS_BETTER: Record<AgeBucketKey, boolean> = {
  last30: false,
  over30: true,
  neverWorked: true,
}

/**
 * The bucket's share of the person's pile, the figure the rank is on: piles differ in
 * size, so ranking the counts would only rank who holds the most claims.
 */
const pileShare = (key: AgeBucketKey) => (p: Person) =>
  p.age && p.openClaims > 0 ? ageCount(p.age, key) / p.openClaims : null

function StandBar({ share, colour, peer }: { share: number; colour: string; peer?: number | null }) {
  const w = Math.max(2, Math.min(100, share * 100))
  return (
    <span className="track">
      <i style={{ width: `${w}%`, background: colour }} />
      {peer != null && (
        <b
          className="peer-tick"
          data-testid="peer-tick"
          style={{ left: `calc(${Math.min(100, Math.max(0, peer * 100))}% - 1px)` }}
        />
      )}
    </span>
  )
}

/** The pile's size: no pile is no figure, never a first place for holding nothing. */
const OPEN_PILE: PileMetric = {
  get: (p) => (p.openClaims > 0 ? p.openClaims : null),
  lowerIsBetter: true,
  format: (v) => int(Math.round(v)),
}

const BUCKET_PILE = (key: AgeBucketKey): PileMetric => ({
  get: pileShare(key),
  lowerIsBetter: AGE_LOWER_IS_BETTER[key],
  format: (v) => pct(v),
})

/** The Open claims row: the count, its bar against the largest pile in the title. */
function OpenClaimsRow({ person, onOpen }: { person: Person; onOpen: (() => void) | null }) {
  const { s, avg } = usePile(person, OPEN_PILE)
  const top = s.max && s.max > 0 ? s.max : null
  return (
    <tr>
      <td className="k">Open claims</td>
      <td className="v">
        <PileCount n={person.openClaims} onOpen={onOpen} what="open claims" />
      </td>
      <td className="bar" title={top ? `Bar: against the largest pile among ${s.group}, ${int(top)}` : undefined}>
        {top && (
          <StandBar share={person.openClaims / top} colour="var(--s1)" peer={avg === null ? null : avg / top} />
        )}
      </td>
      <PeerAvg avg={avg} s={s} format={OPEN_PILE.format} />
      <PileRank s={s} label="Open claims" value={`${int(person.openClaims)} · `} />
    </tr>
  )
}

/** One recency bucket: its count, its share of the pile, the title's average share. */
function BucketRow({
  person,
  bucket,
  label,
  info,
  onOpen,
}: {
  person: Person
  bucket: AgeBucketKey
  label: string
  info: React.ReactNode
  onOpen: (() => void) | null
}) {
  const metric = BUCKET_PILE(bucket)
  const { s, avg } = usePile(person, metric)
  const n = ageCount(person.age!, bucket)
  return (
    <tr>
      <td className="k">
        {label}
        {info}
      </td>
      <td className="v">
        <PileCount n={n} onOpen={onOpen} what={label.toLowerCase()} />
      </td>
      <td className="bar">
        <StandBar share={n / person.openClaims} colour={AGE_COLOUR(bucket)} peer={avg} />
      </td>
      <PeerAvg avg={avg} s={s} format={metric.format} />
      <PileRank s={s} label={label} value={s.value === null ? '' : `${pct(s.value)} of the pile · `} />
    </tr>
  )
}

/**
 * A pile count that opens the claims behind it (review #158: "if i click here it
 * should take me to the corresponding claims"). Plain text when there is nothing to
 * open, or no way to.
 */
function PileCount({ n, onOpen, what }: { n: number; onOpen: (() => void) | null; what: string }) {
  if (n <= 0 || !onOpen) return <>{int(n)}</>
  return (
    <button
      type="button"
      className="cell-link mono"
      data-testid="pile-count"
      onClick={onOpen}
      title={`Show these ${int(n)} ${what}`}
    >
      {int(n)}
    </button>
  )
}

/** Never worked is the one bucket in red; the two worked buckets share a colour. */
const AGE_COLOUR = (key: AgeBucketKey) => (key === 'neverWorked' ? 'var(--crit)' : 'var(--s1)')

/** A worklist row the data does not carry: a dash, never a zero. */
function NoWindow() {
  return (
    <>
      <td className="v" style={{ color: 'var(--ink-3)' }}>
        —
      </td>
      <td className="bar" />
      <td className="r" />
      <td className="r" />
    </>
  )
}

/** The Facilities table's two shares (Yehuda's review of #164: Shayna asked what "Share" meant). */
const OF_THEIR_CLAIMS: Definition = {
  label: '% of their claims',
  text: 'This facility’s share of this person’s billed claims in the range.',
}
const OF_FACILITY_CLAIMS: Definition = {
  label: '% of facility’s claims',
  text:
    'This person’s billed claims at the facility ÷ all billed claims at that facility in the range, ' +
    'of the claims billed by people you can see. With access to some departments only, ' +
    'that is the people in your access, not all of FCC.',
}

const isUnknownFacility = (name: string) => name.trim().toUpperCase() === 'UNKNOWN'

interface FacilityLine {
  facility: string
  group: string
  claims: number
  ofTheirs: number
  /** Null where there is no facility to be a share of: UNKNOWN (Rule 7.2), or an older bundle. */
  ofFacility: number | null
}

const FACILITY_EXPORT: ExportColumn<FacilityLine>[] = [
  { header: 'Facility group', value: (r) => r.group },
  { header: 'Facility', value: (r) => r.facility },
  { header: 'Claims', value: (r) => r.claims },
  { header: '% of their claims', value: (r) => r.ofTheirs },
  { header: '% of facility’s claims', value: (r) => r.ofFacility },
]

/**
 * The person's claims by facility, each with the group it rolls up to, its share of
 * the person's claims and the person's share of the facility's.
 *
 * From `facilityMonths`, as the Facilities page reads it (useFacilityData): the range's
 * own months, and the claim filters exact on a table split by claim cell (#158), else
 * modelled and badged. The facility's total is everyone in that table — for a scoped
 * caller, the people in their grant (the API cuts it; docs/ACCESS.md).
 *
 * On a bundle without it, the crosstab, which is whole-period: under a month range each
 * row is scaled by the person's in-range share, as the Overview's facility bars are, and
 * there is no facility total. UNKNOWN facilities are listed — they are claims — but are
 * not a facility (Rule 7.2), so they have no share of one.
 */
function PersonFacilities({ person }: { person: Person }) {
  // Weighted cells while the Weighted claims switch is on, as the Overview's bars.
  const { derived, crosstab: ct } = useAppData()
  const { fm, months, weightFor, modeled, groupOf } = useFacilityData()
  const rows = useMemo<FacilityLine[]>(() => {
    if (fm) {
      return personFacilityShares(fm, weightFor, person.employee, months)
        .map((r) => {
          const unknown = isUnknownFacility(r.facility)
          return {
            facility: unknown ? 'Unknown' : r.facility,
            group: groupOf.get(r.facility) ?? 'Unknown',
            claims: r.claims,
            ofTheirs: r.ofTheirs,
            ofFacility: unknown ? null : r.ofFacility,
          }
        })
        .filter((r) => Math.round(r.claims) > 0)
    }
    const who = ct.employees.indexOf(person.employee)
    if (who < 0) return []
    const scale = derived.byName.get(person.employee)?.rangeShare.claims ?? 1
    const acc = new Map<number, { group: string; claims: number }>()
    for (const r of ct.rows) {
      if (r[0] !== who) continue
      const e = acc.get(r[5]) ?? { group: ct.facilityGroups[r[4]] ?? 'Unknown', claims: 0 }
      e.claims += r[6] * scale
      acc.set(r[5], e)
    }
    const total = [...acc.values()].reduce((a, e) => a + e.claims, 0)
    return [...acc.entries()]
      .map(([f, e]) => {
        const name = ct.facilities[f] ?? 'UNKNOWN'
        return {
          facility: isUnknownFacility(name) ? 'Unknown' : name,
          group: e.group,
          claims: e.claims,
          ofTheirs: total === 0 ? 0 : e.claims / total,
          ofFacility: null,
        }
      })
      .filter((r) => Math.round(r.claims) > 0)
      .sort((a, b) => b.claims - a.claims)
  }, [fm, weightFor, months, groupOf, ct, derived, person.employee])

  if (rows.length === 0) return null
  const badge = fm && modeled && (
    <span className="modeled" title={`Modelled, not observed.${FACILITY_MODELED[modeled]}`}>
      M
    </span>
  )
  return (
    <section className="section">
      <h3 className="section-heading">
        Facilities
        <Info def={define('facilityClaims')} />
      </h3>
      <ExportBar filename="person-facilities.csv" rows={rows} columns={FACILITY_EXPORT} />
      <div
        className="card"
        style={{ marginTop: 12, padding: 0, overflow: 'auto', maxHeight: 420 }}
      >
        <table className="seven">
          <thead>
            <tr>
              <th>Facility group</th>
              <th>Facility</th>
              <th className="r">Claims{badge}</th>
              <th className="r">
                % of their claims
                <Info def={OF_THEIR_CLAIMS} />
                {badge}
              </th>
              <th className="r">
                % of facility’s claims
                <Info def={OF_FACILITY_CLAIMS} />
                {badge}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.facility}>
                <td>
                  <FacilityLink group={r.group} />
                </td>
                <td style={{ fontWeight: 500 }}>
                  <FacilityLink facility={r.facility} />
                </td>
                <td className="r mono">{int(r.claims)}</td>
                <td className="r mono">{pct(r.ofTheirs)}</td>
                <td
                  className="r mono"
                  title={
                    r.ofFacility !== null
                      ? undefined
                      : r.facility === 'Unknown'
                        ? 'Unknown is not a facility, so there is no facility total to be a share of.'
                        : 'This data has no facility totals by month yet.'
                  }
                >
                  {r.ofFacility === null ? '—' : pct(r.ofFacility)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!fm && derived.narrowed && (
        <p className="body-note" style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
          The facility split is for the whole period; each facility is scaled to the
          person’s share of claims in {monthSpan(derived.months)}.
        </p>
      )}
    </section>
  )
}

/** The Ledger's seven, in its order, each with the scorecard line it is scored on. */
const SEVEN_MEASURES: { key: MetricKey; label: string; scoreKey: ScoreKey }[] = [
  { key: 'costPerClaim', label: 'Cost per claim', scoreKey: 'costPerClaim' },
  { key: 'billedPerMonth', label: 'Billed claims per month', scoreKey: 'claims' },
  { key: 'collectedPct', label: COLLECTION_LABEL, scoreKey: 'collectedPct' },
  { key: 'closedClaimPct', label: 'Claims billed vs resolved %', scoreKey: 'closedClaimPct' },
  { key: 'zeroTouchShare', label: '0% touch share', scoreKey: 'zeroTouchShare' },
  { key: 'openWorkedPct', label: 'Open claims worked in month %', scoreKey: 'openWorkedPct' },
  { key: 'facilities', label: 'Facilities handled', scoreKey: 'facilities' },
]

/** Good at 65% of the maximum, mid at 30%, low at zero or more, bad below — the Ledger's bands. */
const scoreBand = (total: number) =>
  total >= SCORE_MAX * 0.65 ? 'good' : total >= SCORE_MAX * 0.3 ? 'mid' : total >= 0 ? 'low' : 'bad'

/**
 * The Ledger's hero: who this is, who they are ranked against, and the whole-period
 * score as one badge. Shared by the Employee view and the drill-through so the two
 * cannot drift.
 */
function PersonHero({ person }: { person: Person }) {
  const { scope, derived, coverage, volume, trailing } = useAppData()
  // The build's claim-volume warning, for the range's months (review #374,
  // measures/claimVolume.ts).
  const volumeMarks = volume.byPerson.get(person.employee)
  const volumeHere = volumeMarks
    ? derived.months.flatMap((m) => volumeMarks.get(m) ?? [])
    : []
  // Where they stand by score in their own job title, said in words (Shayna,
  // 2026-10-02: "What is her rank? it doesn't tell me.").
  const byScore = scoreStanding(scope.cohorts, person, trailing.byName)
  const period = monthSpan(derived.months)
  const trail = useTrailingCaption()
  const trailingScore = useTrailingScore(person.employee)
  /** Left during the range (or never left): ranked for the months they were active. */
  const wasActive = activeInRange(person)
  // Whole teams, as the Supervisors page builds them: Job title and Employee left off.
  const ranking = useMemo(
    () => teamRanking(scope.supervisorRankPool, derived.byName, 'tree', scope.teamCounts),
    [scope.supervisorRankPool, derived.byName, scope.teamCounts],
  )
  const team = ranking.team(person.employee)
  const teamLine = team
    ? ranking.explain(team, null, availableSupervisorViewMetrics(coverage), period)
    : null
  const openTouches = useOpenTouchDetail()
  const openClaims = useOpenClaimDetail()
  return (
    <div className="mehero">
      <div className="mehero-who">
        <h3>
          {person.employee}
          {/* Paylocity's employment status, off DimEmployee's termination date. */}
          <span
            className={`status-pill ${person.terminated ? 'term' : 'active'}`}
            data-testid="employment-status"
          >
            {person.terminated
              ? `Terminated ${dateLong(person.terminationDate)}`
              : 'Active'}
          </span>
        </h3>
        <div className="mehero-meta">
          {person.jobTitle} · {person.department}
          {/* Review #373: the key other systems know the person by. */}
          {person.paylocityId && (
            <>
              {' · Paylocity ID '}
              <span className="mono" data-testid="paylocity-id">
                {person.paylocityId}
              </span>
            </>
          )}
          {person.supervisor
            ? ` · reports to ${person.supervisor}`
            : person.supervisorOutsideScope
              ? ' · reports to someone outside your access'
              : ''}
          {person.tenureYears !== null ? ` · ${person.tenureYears.toFixed(1)} yrs` : ''}
          {person.terminated && (
            <span
              style={{ color: 'var(--crit-ink)' }}
              title={wasActive ? unscoredReason({ terminated: true }) : NOT_RANKED_TERMINATED}
            >
              {' '}
              · terminated, {wasActive ? 'not scored' : 'not ranked or scored'}
            </span>
          )}
        </div>
        {volumeHere.length > 0 && (
          <p className="caveat" data-testid="claim-volume" style={{ margin: '8px 0 0', fontSize: 'var(--t-rank)' }}>
            <b style={{ color: 'var(--warn-ink)' }}>⚑ Billed-claim volume far above the job title</b> in{' '}
            {volumeHere.map((v, i) => (
              <span key={v.month} title={volumeNote(v)}>
                {i > 0 ? ', ' : ''}
                {monthYear(v.month)} ({int(v.claims)} against a median of {int(Math.round(v.titleMedian))})
              </span>
            ))}
            .{volumeHere[0]!.worklist && (
              <>
                {' '}Most came through “{volumeHere[0]!.worklist}”
                {volumeHere[0]!.worklistShare !== null && <> ({pct(volumeHere[0]!.worklistShare)})</>}, the claim
                matcher’s predicted worklist.
              </>
            )}{' '}
            Volume like this ({volume.rule}) usually comes from claims assigned to the person automatically, not from
            work they did, so those months’ claim figures and anything built on them say little about performance.
            Nothing is left out: they count as they are.
          </p>
        )}
        {/*
          Labor sits up here, once, as facts about the person rather than a section of
          its own, and nothing in it is ranked or compared ("let's not rank their
          labor", 2026-09-23). Pay is from payroll WITHOUT benefit load — not the
          benefit-loaded figure their cost per claim divides. OT and PTO are inside
          total worked, not on top of it. "—" where the bundle predates them.
        */}
        <div className="mehero-meta" data-testid="labor-line">
          Payroll labor {money0(person.labor.payroll)}
          <Info def={define('personPayroll')} />
          {' · '}Total worked {hours(person.labor.worked)} hrs
          <Info def={define('hoursWorked')} />
          {' · '}OT {hours(person.labor.ot)} hrs
          {' · '}PTO {hours(person.labor.pto)} hrs
          {derived.narrowed ? ` in ${monthSpan(derived.months)}` : ' in the period'}
        </div>
        <div className="mehero-meta" data-testid="rank-line">
          {!wasActive
            ? `Not ranked: terminated before ${period}. People are ranked for the months they were active`
            : person.terminated
              ? `Not scored: terminated. Ranked on each measure below for the months they were active in ${period}, against ${byScore.group}.`
              : `${rankText(byScore, period, 'score')}. Every rank on this page is against ${byScore.group}.`}
          {/*
            A supervisor's own claims are a side line — most Medicare Supervisors bill
            none, so their personal rank is often "no cohort". Their team is what they
            are measured on: said here too, so their page always carries a rank where
            the team has one (Shayna, review #62, 2026-10-05: "She should get a rank").
          */}
          {teamLine && <> Their team: {teamLine}.</>}
        </div>
        <div className="mehero-meta" style={{ marginTop: 7 }}>
          <span className="sub2">Underlying rows</span>
          <button
            type="button"
            className="byemp"
            onClick={() => openClaims({ employees: [person.employee], label: person.employee })}
          >
            Claims
          </button>
          <button
            type="button"
            className="byemp"
            onClick={() =>
              openTouches({ employees: [person.employee], label: person.employee, basis: 'claim' })
            }
          >
            Touches
          </button>
        </div>
      </div>
      {/* The range score and the trailing score side by side, each naming its months. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>
        {person.score.scored && (
          <div className={`mescore ${scoreBand(person.score.total)}`} data-testid="range-score">
            <em>score · {period}</em>
            <ScoreTip score={person.score} who={`${person.department} · ${person.jobTitle}`}>
              <b>{formatPoints(person.score.total)}</b>
            </ScoreTip>
            <span>out of {SCORE_MAX}</span>
            <LowVolumeTag lowVolume={person.score.lowVolume} />
          </div>
        )}
        {trailingScore?.scored && (
          <div
            className={`mescore ${scoreBand(trailingScore.total)}`}
            data-testid="trailing-score"
            title={trail.note}
          >
            <em>
              {trail.label.toLowerCase()} · {trail.span}
            </em>
            <ScoreTip score={trailingScore} who={`${person.department} · ${person.jobTitle}`}>
              <b>{formatPoints(trailingScore.total)}</b>
            </ScoreTip>
            <span>out of {SCORE_MAX}</span>
            <LowVolumeTag lowVolume={trailingScore.lowVolume} />
          </div>
        )}
      </div>
    </div>
  )
}

export function PersonDetail({
  person,
  derived,
  openMonths,
}: {
  person: Person
  derived: Derived
  openMonths: string[]
}) {
  const { coverage, scope, label: forBasis, claimBasis } = useAppData()
  const dataset = useLoadedDataset()
  const openClaims = useOpenClaimDetail()
  /**
   * A claims measure's cell, opening its claims (see CLAIM_DRILL): one month's, or the
   * range's when `month` is null. Null where the measure is not billed claims.
   */
  const claimsOf = (key: string, month: string | null): (() => void) | null => {
    const d = claimDrillOf(key, month, derived.months)
    return d && (() => openClaims({ employees: [person.employee], label: person.employee, ...d }))
  }
  /** An Open at month end cell, opening the pile behind it where `opensPile` says. */
  const pileOf = (month: string | null): (() => void) | null => {
    if (!month || !coverage.worklist) return null
    const n = byMonth.get(month)?.openClaims
    const which = pileFor(month, dataset.worklistSnapshot, dataset.worklistMonthEnd, n, person.openClaims)
    if (which === 'today') return () => openPile(null)
    if (which === 'monthEnd') return () => setPileOpen({ bucket: null, monthEnd: { count: n! } })
    return null
  }
  /** The value as a link to its claims, where it has some. */
  const drillable = (text: string, open: (() => void) | null, what: string) =>
    open ? (
      <button
        type="button"
        className="cell-link"
        data-testid="claims-drill"
        style={{ color: 'inherit', fontWeight: 'inherit' }}
        title={`Show the claims behind ${what}`}
        onClick={open}
      >
        {text}
      </button>
    ) : (
      text
    )
  /** The pile behind a worklist count, open in a panel: a bucket, or null for all. */
  const [pileOpen, setPileOpen] = useState<{
    bucket: AgeBucketKey | null
    /** The closed month end's pile (review #401) rather than today's. */
    monthEnd?: { count: number }
  } | null>(null)
  const openPile = (bucket: AgeBucketKey | null) => setPileOpen({ bucket })
  const byMonth = useMemo(
    () => new Map(person.months.map((m) => [m.month, m])),
    [person.months],
  )
  /** The person's rows for the months on screen — what a pooled "Your avg" adds up. */
  const inRange = useMemo(
    () => derived.months.flatMap((m) => byMonth.get(m) ?? []),
    [derived.months, byMonth],
  )
  // Peer bars from the UNFILTERED population, as the Scorecard page builds them.
  const scorer = useMemo(() => monthScorer(derived.people, derived.months), [derived])
  /**
   * Each row's values across the job title, per month — for rank and the colour bar.
   * `pool` is everyone in the title on the books that month (a month row), data or not;
   * `vals` only those with a value. The gap is the month's "no data" (MEASURE-RULES
   * §10.7). The average and the colour stay on `vals`.
   */
  const cohortMonths = useMemo(() => {
    const cohort = scope.cohorts.get(person.jobTitle) ?? []
    const ix = new Map(derived.months.map((m, i) => [m, i]))
    const out = new Map<string, { vals: number[]; pool: number }[]>()
    for (const row of MONTH_ROWS) {
      // Never ranked, so no peer values are gathered for it at all.
      if (row.neutral) continue
      const cols = derived.months.map(() => ({ vals: [] as number[], pool: 0 }))
      for (const c of cohort)
        for (const r of c.months) {
          const i = ix.get(r.month)
          if (i === undefined) continue
          cols[i]!.pool += 1
          // A month with no billed claims is no data that month, as a person with no
          // claims is for the period: its zeros never set the average or a rank.
          const v = r.billedClaims > 0 ? row.get(r) : null
          if (v !== null && Number.isFinite(v)) cols[i]!.vals.push(v)
        }
      out.set(row.key, cols)
    }
    return out
  }, [derived, scope.cohorts, person.jobTitle])
  // The scorecard's own peer group — department + job title, shore-split — not the
  // job-title median. Every other surface compares on that basis; this was the last
  // one that did not, so the same figure read differently here than on Employees.


  /*
   * Score and rank, at the top of the drill-through.
   *
   * Rank is inside the person's own SCORED peer group — same department and job
   * title, the group the points were computed against — not inside whatever the
   * filter bar currently shows. A rank that moved when somebody typed in a filter
   * would be indefensible in the review this page is destined for.
   */
  // Nobody who has left is scored, so there is nothing to move.
  const gaps = useMemo(
    () => (person.score.terminated ? [] : scoreGaps(person.score)),
    [person],
  )

  return (
    <>
          <PersonHero person={person} />
          {/* Reviewers only; renders nothing for anyone else. */}
          <PersonComments name={person.employee} months={derived.months} />

          {/* 2 — the seven measures, as the Ledger tables them */}
          <section className="section" style={{ marginTop: 22, paddingTop: 22 }}>
            <h3 className="section-heading">Seven measures</h3>
            <div className="card" style={{ marginTop: 12, padding: 0, overflow: 'hidden' }}>
              <table className="seven">
                <thead>
                  <tr>
                    <th>Measure</th>
                    <th className="r">Value</th>
                    <th className="r">Rank in title</th>
                    <th className="r">Peer average</th>
                    <th className="r">Scorecard</th>
                  </tr>
                </thead>
                <tbody>
                  {SEVEN_MEASURES.map(({ key, label: raw, scoreKey }) => {
                    const label = forBasis(raw)
                    const metric = METRIC_BY_KEY.get(key)!
                    const s = standing(scope.cohorts, person, metric)
                    const line = person.score.lines.find((l) => l.key === scoreKey)
                    // The scorecard's own peer average, where it has one; "billed per
                    // month" scores the period total, so its average is spread back
                    // over the months. Otherwise the title's median.
                    const avg =
                      line?.peerAvg != null
                        ? key === 'billedPerMonth'
                          ? line.peerAvg / Math.max(1, derived.months.length)
                          : line.peerAvg
                        : s.median
                    const tone =
                      s.band === 'good' ? 'var(--good-ink)' : s.band === 'crit' ? 'var(--crit-ink)' : undefined
                    return (
                      <tr key={key}>
                        <td style={{ fontWeight: 500 }}>
                          {label}
                          <Info def={define(key)} />
                        </td>
                        <td className="r mono" style={{ color: tone, fontWeight: tone ? 600 : undefined }}>
                          {drillable(
                            fmt(metric.format, s.value),
                            s.value === null ? null : claimsOf(key, null),
                            `${label}, ${monthSpan(derived.months)}`,
                          )}
                          {/* The period total, which the masthead carried until it was
                              hidden on this view; shown nowhere else here. */}
                          {key === 'billedPerMonth' && (
                            <span
                              style={{ display: 'block', color: 'var(--ink-3)', fontSize: 11, fontWeight: 400 }}
                            >
                              {int(person.claims)}{' '}
                              {derived.narrowed ? `in ${monthSpan(derived.months)}` : 'in the period'}
                            </span>
                          )}
                        </td>
                        <td
                          className="r mono"
                          title={`${label}: ${rankText(s, monthSpan(derived.months))}${
                            // Someone who left is ranked on their active months only; say
                            // so where a later row (late resolutions) moves the figure shown.
                            person.rankedAs &&
                            fmt(metric.format, metric.get(person.rankedAs)) !== fmt(metric.format, s.value)
                              ? ` — ranked on the months they were active: ${fmt(metric.format, metric.get(person.rankedAs))}`
                              : ''
                          }`}
                        >
                          {s.rank === null ? (
                            <span style={{ color: 'var(--ink-3)', fontSize: rankState(s) === 'noData' ? 11 : undefined }}>
                              {rankState(s) === 'noData' ? rankShort(s) : '—'}
                            </span>
                          ) : (
                            <>
                              {s.rank}
                              <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>/{s.of}</span>
                            </>
                          )}
                        </td>
                        <td className="r mono" style={{ color: 'var(--ink-3)' }}>
                          {fmt(metric.format, avg)}
                        </td>
                        <td className="r">
                          {person.score.terminated ? (
                            <span
                              style={{ color: 'var(--ink-3)', fontSize: 11 }}
                              title={unscoredReason(person.score)}
                            >
                              not scored
                            </span>
                          ) : line ? (
                            line.earned > 0 ? (
                              <span className="pts-chip good">{formatPoints(line.earned)}</span>
                            ) : (
                              <span className="pts-chip neut">0 of {line.pts}</span>
                            )
                          ) : (
                            <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>not scored</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* 2b — what would move the score, as the Ledger's callout cards */}
          <section className="section" style={{ marginTop: 22, paddingTop: 22 }}>
            <h3 className="section-heading">What would move the score</h3>
            {gaps.length > 0 ? (
              <div className="callouts" style={{ marginTop: 12 }}>
                {gaps.map((g) => (
                  <div key={g.key} className="co warn">
                    <h4>
                      {g.label} <span className="pts-chip good">{formatPoints(Math.abs(g.pts))}</span>
                    </h4>
                    <span className="co-big">{g.need}</span>
                    <p>{g.detail}</p>
                  </div>
                ))}
              </div>
            ) : person.score.terminated ? (
              <p className="empty-note" style={{ marginTop: 12 }}>
                {unscoredReason(person.score)}
              </p>
            ) : (
              <div className="co good" style={{ marginTop: 12 }}>
                <h4>Nothing left on the table</h4>
                <p>
                  Every point the scorecard offers is earned
                  {person.score.scored
                    ? ` — ${formatPoints(person.score.total)} of ${SCORE_MAX}`
                    : ''}
                  .
                </p>
              </div>
            )}
          </section>

          {/* 3 — month by month, as the Ledger tables it */}
          <section className="section">
            <h3 className="section-heading">Month by month</h3>
            {person.months.length === 0 ? (
              <p className="empty-note">No month rows for this person.</p>
            ) : (
              <div className="mtwrap" style={{ marginTop: 12 }}>
                {claimBasis === 'weighted' && (
                  <p className="body-note" style={{ margin: '0 0 8px' }}>
                    These months count standard claims. Weighted claims is on, so the cost
                    per claim and billed claims above divide by weighted claims and will
                    not match the rows here.
                  </p>
                )}
                <table className="mtab">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      {derived.months.map((m) => (
                        <th key={m} className={openMonths.includes(m) ? 'partial' : undefined}>
                          {MONTH_LABEL(m)}
                          {openMonths.includes(m) ? ' *' : ''}
                        </th>
                      ))}
                      <th className="avgcol">Your avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MONTH_ROWS.map((row) => {
                      const vals = derived.months.map((m) => {
                        const r = byMonth.get(m)
                        return r ? row.get(r) : null
                      })
                      const present = vals.filter((v): v is number => v !== null && Number.isFinite(v))
                      // Collection's months are overlapping windows, so their mean counts
                      // most bills several times; the range's own window is the figure.
                      const own =
                        row.key === 'coll'
                          ? person.collectedPct
                          : row.pooled
                            ? row.pooled(inRange)
                            : present.length
                              ? present.reduce((a, b) => a + b, 0) / present.length
                              : null
                      const rule = MONTH_RULES.find((r) => r.key === row.score)
                      return (
                        <tr key={row.key}>
                          <td>
                            {row.label}
                            <Info def={define(ROW_DEFINITION[row.key])} />
                          </td>
                          {vals.map((v, i) => {
                            const month = derived.months[i]!
                            const partial = openMonths.includes(month)
                            if (v === null || !Number.isFinite(v))
                              return (
                                <td key={i} className={`v empty${partial ? ' partial' : ''}`}>
                                  —
                                </td>
                              )
                            if (row.neutral)
                              return (
                                <td key={i} className={`v neut${partial ? ' partial' : ''}`}>
                                  {row.format(v)}
                                </td>
                              )
                            const col = cohortMonths.get(row.key)?.[i]
                            const peers = col?.vals ?? []
                            const hasPeers = peers.length >= MIN_COHORT
                            const { ref, basis } = monthCellReference(
                              rule ? scorer.barFor(person, rule, i) : null,
                              peers,
                              own,
                            )
                            // Ranked in every month they were active (review #61, 2026-10-05);
                            // never in a month after they left, nor in a month they billed
                            // nothing (no data, at the bottom).
                            const billedThisMonth = (byMonth.get(month)?.billedClaims ?? 0) > 0
                            const activeThisMonth = activeInMonth(person, month)
                            const ranked = hasPeers && activeThisMonth && billedThisMonth
                            const tone = cellTone(v, ref, row.higherIsBetter)
                            const cls =
                              tone.bg === 'var(--good-bg)' ? 'good' : tone.bg === 'var(--crit-bg)' ? 'bad' : 'neut'
                            const rank = ranked
                              ? (row.higherIsBetter
                                  ? peers.filter((x) => x > v).length
                                  : peers.filter((x) => x < v).length) + 1
                              : null
                            const mp = rule ? scorer.rulePoints(person, rule, byMonth.get(month), i) : null
                            const pool = Math.max(col?.pool ?? 0, peers.length)
                            const facts = {
                              rank,
                              of: pool,
                              noData: pool - peers.length,
                              hasValue: billedThisMonth,
                              floor: MIN_COHORT,
                              group: titlePlural(person.jobTitle),
                              terminated: !activeThisMonth,
                            }
                            const open = row.key === 'openme' ? pileOf(month) : claimsOf(row.key, month)
                            const note = row.key === 'openme' && !open ? PILE_NOTE : undefined
                            return (
                              <td
                                key={i}
                                className={`v ${cls}${partial ? ' partial' : ''}${mp || rank ? ' haspt' : ''}`}
                                title={`${row.label} in ${MONTH_LABEL(month)}: ${row.format(v)} · ${activeThisMonth ? rankText(facts, monthYear(month)) : `Not ranked: terminated before ${monthYear(month)}`}${
                                  basis === 'peer'
                                    ? ` · peer average ${row.format(ref)}`
                                    : basis === 'title'
                                      ? ` · job title average ${row.format(ref)}`
                                      : ` · your average ${row.format(ref)}`
                                }${mp ? ` · would score ${formatPoints(mp.pts)}` : ''}${
                                  row.key === 'coll' ? ` · reads ${describeWindow(collectionWindow(month, month))}` : ''
                                }${open ? ' · click for the claims' : note ? ` · ${note}` : ''}`}
                              >
                                {mp && (
                                  <span className={`mpt ${mp.pts > 0 ? 'pos' : mp.pts < 0 ? 'neg' : 'zero'}`}>
                                    {formatPoints(mp.pts)}
                                  </span>
                                )}
                                {rank !== null && (
                                  <span className="mrk">
                                    {rank}
                                    <i>/{pool}</i>
                                  </span>
                                )}
                                {drillable(row.format(v), open, `${row.label} in ${monthYear(month)}`)}
                              </td>
                            )
                          })}
                          <td className="v avgcol">
                            {drillable(
                              row.format(own),
                              own === null ? null : claimsOf(row.key, null),
                              `${row.label}, ${monthSpan(derived.months)}`,
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {/*
                  What the colours and the small corner numbers mean — Shayna, 2026-09-25.
                  Two rows of key items, each swatch held to its own words: as one run of
                  text the swatches drifted from their labels as it wrapped (#13,
                  2026-10-02).
                */}
                <div className="grid-key-rows">
                  <div className="grid-key">
                    <b>Colours</b>
                    <span className="key-item">
                      <span className="key-sw good" />
                      10%+ better than the peer average
                    </span>
                    <span className="key-item">
                      <span className="key-sw bad" />
                      10%+ worse
                    </span>
                    <span className="key-item">
                      <span className="key-sw" />
                      within 10%
                    </span>
                  </div>
                  <div className="grid-key">
                    <span className="key-item">
                      <b>Corner figures</b> points earned (left), rank in job title (right)
                    </span>
                    <span className="key-item">
                      <b>Your avg</b> rates pooled over the months (total ÷ total, as the
                      figures above); counts averaged
                    </span>
                    <span className="key-item">
                      <b>Peer average</b> the scorecard’s, that each month’s points are
                      scored on; rows without points use the job title’s average that
                      month, or your own average under 5 in a title
                    </span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* 4 — open worklist, as the Ledger's ruled table. Only for people who have a pile. */}
          {coverage.worklist && person.openClaims > 0 && (
            <section className="section">
              <h3 className="section-heading">Open worklist today</h3>
              <table className="standtable" style={{ marginTop: 12 }}>
                <tbody>
                  <OpenClaimsRow person={person} onOpen={openPile ? () => openPile(null) : null} />
                  {/*
                    The pile by when ANYONE last touched it, counted back from the
                    worklist date. The four rows add up to the open claims above, and
                    are the only recency rows (MEASURE-RULES §5.2a).
                  */}
                  {/*
                    Rank in the job title, not the share of the pile: Shayna's review
                    #43 (2026-10-02), "instead of percentile, take that out and include a
                    rank against job title". The share is on the bar and on hover, with
                    the title's average share ticked on it and written beside it (#159);
                    the rank is on that share, as every other rank (`standing`, §10.7).
                  */}
                  {AGE_BUCKETS.map(({ key, label, def }) =>
                    coverage.worklistAge && person.age ? (
                      <BucketRow
                        key={key}
                        person={person}
                        bucket={key}
                        label={label}
                        info={<Info def={define(def)} />}
                        onOpen={openPile ? () => openPile(key) : null}
                      />
                    ) : (
                      <tr key={key}>
                        <td className="k">
                          {label}
                          <Info def={define(def)} />
                        </td>
                        <NoWindow />
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
              {coverage.worklistAge && dataset.worklistAsOf ? (
                <p className="body-note" style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                  Last worked by anyone, counted back from {dateLong(dataset.worklistAsOf)}.
                  {dataset.touchHistoryFrom &&
                    ` Touch records begin ${dateLong(dataset.touchHistoryFrom)}.`}
                </p>
              ) : (
                !coverage.worklistAge && (
                  <p className="body-note" style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)' }}>
                    How recently the pile was worked needs each open claim’s last touch,
                    which this data does not carry.
                  </p>
                )
              )}
            </section>
          )}
          {/* Outside the section: a month-end pile opens for someone with none today. */}
          {pileOpen && (
            <OpenPilePanel
              person={person}
              bucket={pileOpen.bucket}
              asOf={pileOpen.monthEnd ? null : (dataset.worklistAsOf ?? null)}
              monthEnd={pileOpen.monthEnd ?? null}
              onBucket={(bucket) => setPileOpen((p) => ({ ...p, bucket }))}
              onClose={() => setPileOpen(null)}
            />
          )}

          {/* 5 — where the claims were, by facility and facility group */}
          <PersonFacilities person={person} />

    </>
  )
}
